import { Button, Tooltip } from "flowbite-react";
import { HiEye, HiPencil, HiTrash, HiBan, HiRefresh } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Group, DataGridPagination } from "@/sharedTypes";
import DataTable from "../Table";
import GroupFormDialog from "./GroupFormDialog";
import {
  useUpdateGroupMutation,
  useDisableGroupMutation,
  useRestoreGroupMutation,
  useHardDeleteGroupMutation,
} from "@/api/groupApi/group";
import { toast } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

interface GroupTableProps {
  data: Group[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  showInactive: boolean;
}

const GroupTable = ({ data, pagination, onPageChange, showInactive }: GroupTableProps) => {
  const { t } = useTranslation(["group"]);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | undefined>(undefined);
  
  const [updateGroup] = useUpdateGroupMutation();
  const [disableGroup] = useDisableGroupMutation();
  const [restoreGroup] = useRestoreGroupMutation();
  const [hardDeleteGroup] = useHardDeleteGroupMutation();

  const handleEditClick = (group: Group) => {
    setSelectedGroup(group);
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (updatedData: Group) => {
    if (!selectedGroup) return;
    try {
      // Create a clean payload with only allowed fields
      const payload: Partial<Group> = {
          group_name: updatedData.group_name,
          group_description: updatedData.group_description
      };

      await updateGroup({
        groupId: selectedGroup.group_id.toString(),
        updatedDetails: payload,
      }).unwrap();
      toast.success(t("group:updateSuccess"));
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error(t("group:updateFail"));
    }
  };

  const handleDisableClick = async (id: string) => {
    if (confirm(t("group:disableConfirm"))) {
        try {
            await disableGroup(id).unwrap();
            toast.success(t("group:disableSuccess"));
        } catch (error) {
            toast.error(t("group:disableFail"));
        }
    }
  };

  const handleRestoreClick = async (id: number) => {
      try {
          await restoreGroup(id).unwrap();
          toast.success(t("group:restoreSuccess"));
      } catch (error) {
          toast.error(t("group:restoreFail"));
      }
  };

  const handleHardDeleteClick = async (id: number) => {
     if (confirm(t("group:deleteConfirm"))) {
        try {
            await hardDeleteGroup(id).unwrap();
            toast.success(t("group:deleteSuccess"));
        } catch (error) {
            toast.error(t("group:deleteFail"));
        }
     }
  };

  const columns = [
    { key: "group_name", label: t("group:groupName") },
    {
      key: "group_description",
      label: t("group:description"),
      render: (row: Group) => (
        <span>
          {row.group_description?.length > 50
            ? `${row.group_description.slice(0, 50)}...`
            : row.group_description || ""}
        </span>
      ),
    },
    {
      key: "actions",
      label: t("group:actions"),
      render: (row: Group) => (
        <div className="flex gap-2">
            {!showInactive ? (
                <>
                    <Tooltip content={t("group:view")}>
                        <Button color="light" size="xs" onClick={() => row.group_id && navigate(`/groups/${row.group_id}`)}>
                        <HiEye className="h-4 w-4 text-blue-500" />
                        </Button>
                    </Tooltip>
                    {isCompanyAdminUser(user) && (
                      <>
                        <Tooltip content={t("group:edit")}>
                            <Button color="light" size="xs" onClick={() => row.group_id && handleEditClick(row)}>
                            <HiPencil className="h-4 w-4 text-purple-500" />
                            </Button>
                        </Tooltip>
                        <Tooltip content={t("group:disable")}>
                            <Button color="light" size="xs" onClick={() => row.group_id && handleDisableClick(row.group_id.toString())}>
                            <HiBan className="h-4 w-4 text-orange-500" />
                            </Button>
                        </Tooltip>
                      </>
                    )}
                </>
            ) : (
                <>
                  {isCompanyAdminUser(user) && (
                    <>
                      <Tooltip content={t("group:restore")}>
                          <Button color="light" size="xs" onClick={() => row.group_id && handleRestoreClick(row.group_id)}>
                          <HiRefresh className="h-4 w-4 text-green-500" />
                          </Button>
                      </Tooltip>
                      <Tooltip content={t("group:permanentDelete")}>
                          <Button color="light" size="xs" onClick={() => row.group_id && handleHardDeleteClick(row.group_id)}>
                          <HiTrash className="h-4 w-4 text-red-500" />
                          </Button>
                      </Tooltip>
                    </>
                  )}
                </>
            )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />
      <GroupFormDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={handleEditSubmit}
        mode="edit"
        initialData={selectedGroup}
      />
    </>
  );
};

export default GroupTable;
